if (!customElements.get('mm-footer-streams')) {
  customElements.define('mm-footer-streams', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.clock = 0;
      this.last = 0;
      this.visible = false;
      const make = (tag, attributes, parent) => {
        const element = document.createElementNS('http://www.w3.org/2000/svg', tag);
        Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
        parent.append(element);
        return element;
      };
      this.replaceChildren();
      this.svg = make('svg', { width: '100%', height: '100%', focusable: 'false' }, this);
      const defs = make('defs', {}, this.svg);
      const prefix = 'mm-drip-' + crypto.randomUUID();
      this.drips = Array.from({ length: 3 }, (_, index) => {
        const gradient = make('linearGradient', { id: prefix + '-trail-' + index, gradientUnits: 'userSpaceOnUse', x1: 0, x2: 0 }, defs);
        [[0, 0], [0.25, 0.06], [0.55, 0.35], [1, 1]].forEach(([offset, opacity]) =>
          make('stop', { offset, 'stop-color': 'var(--mm-footer-chocolate)', 'stop-opacity': opacity }, gradient));
        const glaze = make('radialGradient', { id: prefix + '-head-' + index, cx: '32%', cy: '35%', r: '75%' }, defs);
        [[0, 'var(--mm-footer-shine)'], [0.4, 'var(--mm-footer-chocolate)'], [1, '#3c231b']].forEach(([offset, color]) =>
          make('stop', { offset, 'stop-color': color }, glaze));
        const group = make('g', { opacity: 0 }, this.svg);
        const trail = make('path', { fill: 'none', stroke: 'url(#' + prefix + '-trail-' + index + ')', 'stroke-width': 16, 'stroke-linecap': 'round' }, group);
        const head = make('g', {}, group);
        make('path', { d: 'M0-13C-3-13-8-4-8 2C-8 14 8 14 8 2C8-4 3-13 0-13Z', fill: 'url(#' + prefix + '-head-' + index + ')' }, head);
        make('path', { d: 'M-3-3Q-5 1-4 5', fill: 'none', stroke: 'var(--mm-footer-shine)', 'stroke-width': 1.7, 'stroke-linecap': 'round', opacity: 0.65 }, head);
        return { group, trail, head, gradient, start: index * 3300, position: null };
      });
      this.resize = new ResizeObserver(() => this.measure());
      this.resize.observe(this);
      this.resize.observe(this.parentElement.querySelector('.mm-footer__inner'));
      document.fonts.ready.then(() => { if (this.isConnected) this.measure(); });
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.run();
      });
      this.observer.observe(this);
      this.motion.addEventListener('change', () => this.run(), { signal: this.controller.signal });
      document.addEventListener('visibilitychange', () => this.run(), { signal: this.controller.signal });
    }

    measure() {
      this.width = this.clientWidth;
      this.height = this.clientHeight;
      const bounds = this.getBoundingClientRect();
      const content = this.parentElement.querySelector('.mm-footer__inner');
      const protectedRects = [...content.querySelectorAll('.mm-footer__logo')].map((element) => element.getBoundingClientRect());
      const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        if (!walker.currentNode.textContent.trim()) continue;
        const range = document.createRange();
        range.selectNodeContents(walker.currentNode);
        protectedRects.push(...range.getClientRects());
      }
      this.lanes = [];
      for (const tip of [774, 950, 1106, 1272]) {
        const x = tip / 1440 * this.width;
        if (protectedRects.every((rect) => !rect.width || x < rect.left - bounds.left - 12 || x > rect.right - bounds.left + 12)) this.lanes.push(x / this.width);
      }
      const border = this.parentElement.querySelector('.mm-footer__chocolate-fill');
      this.borderHeight = border.ownerSVGElement.getBoundingClientRect().height;
      const length = border.getTotalLength();
      this.edge = [];
      for (let offset = 0; offset <= length; offset += 1) {
        const point = border.getPointAtLength(offset);
        this.edge.push({ x: point.x, y: point.y });
      }
      this.drips.forEach((drip) => {
        drip.group.setAttribute('opacity', 0);
        drip.position = null;
        drip.start = this.clock + this.drips.indexOf(drip) * 3300;
      });
    }

    anchor(drip) {
      const target = drip.position * 1440;
      const candidates = this.edge.filter((point) => Math.abs(point.x - target) < 2);
      drip.origin = Math.max(0, ...candidates.map((point) => point.y)) / 92 * this.borderHeight - 4;
    }

    place(drip) {
      const available = this.lanes.filter((position) => this.drips.every((other) => other.position === null || Math.abs(other.position - position) > 0.01));
      if (!available.length) return false;
      drip.position = available[Math.floor(Math.random() * available.length)];
      this.anchor(drip);
      return true;
    }

    run() {
      cancelAnimationFrame(this.frame);
      this.last = 0;
      this.svg.style.display = this.motion.matches ? 'none' : 'block';
      if (!this.visible || this.motion.matches || document.hidden) return;
      this.frame = requestAnimationFrame((time) => this.tick(time));
    }

    tick(time) {
      if (this.last) this.clock += Math.min(time - this.last, 64);
      this.last = time;
      if (this.edge && this.lanes.length) {
        const duration = this.height > 700 ? 12500 : 9500;
        this.drips.forEach((drip) => {
          let age = this.clock - drip.start;
          if (age < 0) return;
          if (drip.position === null) {
            if (!this.place(drip)) return;
            drip.start = this.clock;
            age = 0;
          }
          const solidTrail = Math.max(1, this.height * 0.5 - drip.origin);
          const fadeLength = this.height * 0.25;
          const length = solidTrail + fadeLength;
          const release = Math.min(age / 1300, 1);
          const distance = age < 1300 ? 16 * release * release : 16 + Math.pow((age - 1300) / duration, 1.15) * (this.height - drip.origin);
          const y = drip.origin + distance;
          if (y > this.height + length + 24) {
            drip.group.setAttribute('opacity', 0);
            drip.position = null;
            drip.start = this.clock + 900 + Math.random() * 1600;
            return;
          }
          const x = drip.position * this.width;
          const tail = Math.max(drip.origin - 5, y - length);
          const path = 'M' + x + ' ' + tail + 'L' + x + ' ' + y;
          drip.trail.setAttribute('d', path);
          drip.gradient.setAttribute('y1', y - length);
          drip.gradient.setAttribute('y2', y - solidTrail);
          drip.head.setAttribute('transform', 'translate(' + x + ' ' + y + ') scale(' + (0.65 + release * 0.35) + ' ' + (0.55 + release * 0.45) + ')');
          drip.trail.setAttribute('stroke-width', 16 * (0.65 + release * 0.35));
          drip.group.setAttribute('opacity', 1);
        });
      }
      this.frame = requestAnimationFrame((next) => this.tick(next));
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.controller?.abort();
      this.resize?.disconnect();
      this.observer?.disconnect();
    }
  });
}
